import { Components, createTheme, Theme } from '@mui/material/styles';
import { axisClasses, legendClasses, lineClasses, PieChartProps, pieClasses, RadarChartProps } from '@mui/x-charts';
import { DEFAULT_X_AXIS_KEY, DEFAULT_Y_AXIS_KEY } from '@mui/x-charts/constants';

import { getChartPalette } from 'components/charts/constants/chart-palettes';
import { ChartLocalization } from 'components/charts/types/chart-localization.type';
import { ChartMode, ChartSettings } from 'components/charts/types/chart-settings.type';

declare module '@mui/material/styles' {
  // Pie and Radar read theme default props at runtime, but MUI X 9.15 does not type them.
  interface Components<Theme = unknown> {
    MuiPieChart?: { defaultProps?: Partial<PieChartProps> };
    MuiRadarChart?: { defaultProps?: Partial<RadarChartProps> };
  }
}

export const createChartTheme = (
  baseTheme: Theme,
  settings: ChartSettings | undefined,
  localization: ChartLocalization,
  modeOverride?: ChartMode
): Theme => {
  const mode = modeOverride ?? settings?.mode ?? baseTheme.palette.mode;
  const palette = getChartPalette(mode, settings?.palette ?? 'verified');
  const { backgrounds, borders, text } = palette;
  const isCompact = settings?.density === 'compact';
  const gridlinesEnabled = settings?.gridlinesEnabled ?? true;
  const legendDefaults = settings ? { hideLegend: !settings.legendsEnabled } : {};
  // MUI X chart margins are pixel values, not theme spacing multipliers.
  const cartesianMargin = {
    left: isCompact ? 12 : 16,
    right: isCompact ? 3 : 4,
    top: isCompact ? 3 : 4,
    bottom: isCompact ? 7 : 10,
  } as const;
  // Native charts already honor the OS reduced-motion preference; this only covers the theme's own transitions.
  const reducedMotion = settings ? !settings.animationsEnabled || settings.prefersReducedMotion : false;
  const {
    mode: _outerMode,
    background: _outerBackground,
    text: _outerText,
    divider: _outerDivider,
    action: _outerAction,
    chart: _outerChart,
    ...brandPalette
  } = baseTheme.palette;

  const components: Components<Theme> = {
    MuiChartsXAxis: {
      defaultProps: { disableLine: true, disableTicks: true },
      styleOverrides: {
        root: {
          [`& .${axisClasses.tickLabel}`]: {
            fill: text.secondary,
            fontFamily: baseTheme.typography.fontFamily,
            fontSize: isCompact ? 11 : 12,
          },
          [`& .${axisClasses.label}`]: {
            fill: text.secondary,
            fontFamily: baseTheme.typography.fontFamily,
            fontSize: isCompact ? 12 : 13,
            fontWeight: 500,
          },
          [`&.${axisClasses.directionX} .${axisClasses.tickLabel}`]: { transform: 'translateY(6px)' },
        },
      },
    },
    MuiChartsYAxis: {
      defaultProps: { disableLine: true, disableTicks: true },
      styleOverrides: {
        root: {
          [`& .${axisClasses.tickLabel}`]: {
            fill: text.secondary,
            fontFamily: baseTheme.typography.fontFamily,
            fontSize: isCompact ? 11 : 12,
          },
          [`& .${axisClasses.label}`]: {
            fill: text.secondary,
            fontFamily: baseTheme.typography.fontFamily,
            fontSize: isCompact ? 12 : 13,
            fontWeight: 500,
          },
          [`&.${axisClasses.directionY} .${axisClasses.tickLabel}`]: { transform: 'translateX(-3px)' },
        },
      },
    },
    MuiChartsGrid: {
      styleOverrides: {
        line: {
          stroke: borders.subtle,
          strokeWidth: 1,
        },
      },
    },
    MuiChartsTooltip: {
      styleOverrides: {
        paper: {
          backgroundColor: backgrounds.raised,
          border: `1px solid ${borders.subtle}`,
          borderRadius: 8,
          boxShadow: palette.shadow,
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiChartsLegend: {
      defaultProps: { direction: 'horizontal' },
      styleOverrides: {
        root: {
          gap: baseTheme.spacing(isCompact ? 3 : 5),
          [`& .${legendClasses.mark}`]: { width: isCompact ? 8 : 10, height: isCompact ? 8 : 10, borderRadius: 3 },
          [`& .${legendClasses.label}`]: { fontSize: isCompact ? 12 : 13, fontWeight: 500, color: text.primary },
          [`& button.${legendClasses.series}`]: { cursor: 'pointer' },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: backgrounds.raised,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: backgrounds.sunken,
            color: text.primary,
          },
          '&.Mui-selected': {
            backgroundColor: backgrounds.sunken,
            color: text.primary,
            '&:hover, &.Mui-focusVisible': {
              backgroundColor: backgrounds.sunken,
              color: text.primary,
            },
          },
        },
      },
    },
    MuiChartsLocalizationProvider: {
      defaultProps: { localeText: localization.localeText },
    },
    MuiChartsDataProvider: {
      defaultProps: {
        colors: palette.series.categorical,
        ...(settings ? { skipAnimation: !settings.animationsEnabled } : {}),
        // Used only when a chart supplies no axis of its own, such as Scatter or a horizontal Bar's value axis.
        xAxis: [{ id: DEFAULT_X_AXIS_KEY, scaleType: 'linear', valueFormatter: localization.axisValueFormatter }],
        yAxis: [{ id: DEFAULT_Y_AXIS_KEY, scaleType: 'linear', valueFormatter: localization.axisValueFormatter }],
      },
    },
    MuiBarChart: {
      defaultProps: {
        ...legendDefaults,
        borderRadius: 6,
        grid: { horizontal: gridlinesEnabled },
        margin: cartesianMargin,
      },
    },
    MuiLineChart: {
      defaultProps: { ...legendDefaults, grid: { horizontal: gridlinesEnabled }, margin: cartesianMargin },
    },
    MuiScatterChart: {
      defaultProps: { ...legendDefaults, grid: { horizontal: gridlinesEnabled, vertical: gridlinesEnabled } },
    },
    MuiPieChart: {
      defaultProps: legendDefaults,
    },
    MuiRadarChart: {
      defaultProps: legendDefaults,
    },
    MuiPieArcLabelPlot: {
      styleOverrides: {
        root: {
          [`& .${pieClasses.arcLabel}`]: {
            fontSize: 11,
            fontWeight: 700,
            paintOrder: 'stroke',
            stroke: backgrounds.surface,
            strokeWidth: 3,
            strokeLinejoin: 'round',
          },
        },
      },
    },
    MuiLinePlot: {
      styleOverrides: {
        root: {
          [`& .${lineClasses.line}`]: { strokeWidth: 2.5, strokeLinecap: 'round' },
        },
      },
    },
    MuiMarkPlot: {
      styleOverrides: {
        root: {
          [`& .${lineClasses.mark}`]: {
            r: 4,
            transition: `r ${reducedMotion ? 0 : 160}ms ${baseTheme.transitions.easing.easeInOut}`,
            '&[data-highlighted]': { r: 6 },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          },
        },
      },
    },
    MuiGauge: {
      styleOverrides: {
        valueArc: { fill: palette.series.categorical[0] },
        referenceArc: { fill: backgrounds.sunken },
        valueText: { fill: text.primary, fontSize: 28, fontWeight: 700 },
      },
    },
  };

  return createTheme(
    {
      ...baseTheme,
      palette: {
        ...brandPalette,
        mode,
        background: {
          default: backgrounds.canvas,
          paper: backgrounds.surface,
        },
        text: {
          primary: text.primary,
          secondary: text.secondary,
          disabled: text.disabled,
        },
        divider: borders.subtle,
        chart: palette,
      },
    },
    {
      ...(reducedMotion
        ? {
            transitions: {
              ...baseTheme.transitions,
              duration: {
                shortest: 0,
                shorter: 0,
                short: 0,
                standard: 0,
                complex: 0,
                enteringScreen: 0,
                leavingScreen: 0,
              },
            },
          }
        : {}),
      components,
    }
  );
};
